import Plugin from '@ckeditor/ckeditor5-core/src/plugin';

// for reference
// https://github.com/ckeditor/ckeditor5/tree/master/packages/ckeditor5-list

class ListMarkerSize extends Plugin {
  init() {
    this.setupConversion();
    this.setupFontSizeCommandListener();
  }

  setupFontSizeCommandListener() {
    const editor = this.editor;
    const fontSizeCommand = editor.commands.get('fontSize');

    fontSizeCommand.on('execute', (event) => {
      const model = editor.model;
      const document = model.document;
      const selection = document.selection;

      const fontSizeValue = parseInt(event.source.value, 10);

      const updateListItemAttribute = (writer) => {
        // get list item blocks from selection (paragraphs in the model)
        const blocks = Array.from(selection.getSelectedBlocks())
          .filter(isListItemBlock);

        for (const block of blocks) {
          if (fontSizeValue) {
            writer.setAttribute('dataMarkerSize', fontSizeValue, block);
          } else {
            writer.removeAttribute('dataMarkerSize', block);
          }
        }
      };

      editor.model.change((writer) => {
        updateListItemAttribute(writer);
      });
    });
  }

  setupConversion() {
    const editor = this.editor;

    editor.conversion.for('editingDowncast').add((dispatcher) => {
      dispatcher.on('attribute:dataMarkerSize', (evt, data, conversionApi) => {
        this.downcastConverter(evt, data, conversionApi);
      });
    });

    editor.conversion.for('dataDowncast').add((dispatcher) => {
      dispatcher.on('attribute:dataMarkerSize', (evt, data, conversionApi) => {
        this.downcastConverter(evt, data, conversionApi);
      });
    });

    editor.conversion.for('upcast').add((dispatcher) => {
      dispatcher.on('element:li', (evt, data, conversionApi) => {
        this.upcastConverter(evt, data, conversionApi);
      }, { priority: 'low' });
    });
  }

  downcastConverter(evt, data, conversionApi) {
    const { writer, mapper, consumable } = conversionApi;

    const listItem = data.item;
    const attributeNames = ['dataMarkerSize'];

    if (!attributeNames.includes(data.attributeKey)) {
      return;
    }

    if (!consumable.consume(listItem, 'attribute:dataMarkerSize')) {
      return;
    }

    const viewElement = mapper.toViewElement(listItem); // p element
    const parentElement = viewElement.parent; // li element (only available in view)
    
    if (!isListItemView(parentElement)) {
      // This case is possible when we add a list item, 
      // but press Enter twice and add an empty paragraph instead.
      // So we need to remove the attribute from the model of listItem
      // as it is no longer a list item.
      this.editor.model.enqueueChange((writer) => {
        writer.removeAttribute('dataMarkerSize', listItem);
      });
      return;
    }

    if (data.attributeNewValue !== null) {
      writer.setAttribute('data-marker-size', data.attributeNewValue, parentElement);
    } else {
      writer.removeAttribute('data-marker-size', parentElement);
    }
  }

  // As we process HTML before loading it into CK, 
  // li elements already have 'data-marker-size' attribute. 
  // This converter is used to update the element model 
  // if it already has 'data-marker-size' attribute.
  upcastConverter(evt, data, conversionApi) {
    const { writer, schema } = conversionApi;

    if (!data.modelRange) {
      return;
    }

    const items = Array.from(data.modelRange.getItems({ shallow: true }))
      .filter((item) => schema.checkAttribute(item, 'listItemId'));

    if (!items.length) {
      return;
    }

    const dataMarkerSize = data.viewItem.getAttribute('data-marker-size');

    for (const item of items) {
      if (item.hasAttribute('dataMarkerSize')) continue;
      if (dataMarkerSize) writer.setAttribute('dataMarkerSize', dataMarkerSize, item);
    }
  }
}

function isListItemBlock(node) {
  return !!node && node.is('element') && node.hasAttribute('listItemId');
}

function isListItemView(viewElement) {
  return viewElement.is('element', 'li');
}

export default ListMarkerSize;
